import {
  Phase2,
  AgFinancingFilesContext,
  SerasaReport,
  CreditInput,
  DebtInput,
  AgFinancingDebtType,
  SerasaApiStatus,
  OpportunityContactRole
} from '@indigo-ag/schema';
import i18next from 'i18next';

import {
  IParticipationInfo,
  IClientData,
  IPesquisaSerasaInfo,
  IIncomeStatementInfo,
  IExploredAreasInfo,
  IFinancialInformation,
  IConsultingScore,
  INotes,
  IJudicialActionsInfo,
  brazil
} from 'shared';

export const getParticipationInfoFromCredit = (credit: Phase2['credit']): IParticipationInfo | undefined => {
  if (credit) {
    const { shareholderPartner, shareholderCompany } = credit;

    if (shareholderPartner?.length || shareholderCompany?.length) {
      return {
        shareholderPartner,
        shareholderCompany
      };
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

export const getClientDataInfoFromCredit = (credit: Phase2['credit']): IClientData | undefined => {
  if (credit) {
    const { customerActivityType, customerProducts, customerDescription } = credit;

    if (customerActivityType || customerDescription || customerProducts?.length) {
      return {
        customerActivityType,
        customerProducts,
        customerDescription
      };
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

export const getPesquisaSerasaInfoFromCredit = (credit: Phase2['credit']): IPesquisaSerasaInfo | undefined => {
  if (credit) {
    const pesquisaSerasaFiles = credit.files
      ? credit.files.filter(f => f.context === AgFinancingFilesContext.PESQUISA_SERASA)
      : [];
    const { serasaConsultationDate, serasaObservation, serasaDebts } = credit;

    if (serasaConsultationDate || serasaObservation || pesquisaSerasaFiles.length || serasaDebts?.length) {
      return {
        serasaConsultationDate,
        serasaDebts,
        serasaObservation,
        files: pesquisaSerasaFiles
      };
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

export const getIncomeStatementInfoFromCredit = (credit: Phase2['credit']): IIncomeStatementInfo | undefined => {
  if (credit) {
    const {
      returnOfIncomeCosting,
      returnOfIncomeDebts,
      returnOfIncomeGrossRevenue,
      returnOfIncomeInvestment,
      returnOfIncomePatrimony,
      returnOfIncomeResult,
      returnOfIncomeRural,
      returnOfIncomeTaxableIncome,
      returnOfIncomeUrban,
      returnOfIncomeYearIRPF,
      returnOfIncomeExploredAreaCode,
      returnOfIncomeExploredHectares
    } = credit;

    if (
      returnOfIncomeCosting ||
      returnOfIncomeDebts ||
      returnOfIncomeGrossRevenue ||
      returnOfIncomeInvestment ||
      returnOfIncomePatrimony ||
      returnOfIncomeResult ||
      returnOfIncomeRural ||
      returnOfIncomeTaxableIncome ||
      returnOfIncomeUrban ||
      returnOfIncomeYearIRPF ||
      returnOfIncomeExploredAreaCode ||
      returnOfIncomeExploredHectares
    ) {
      return {
        returnOfIncomeCosting,
        returnOfIncomeDebts,
        returnOfIncomeGrossRevenue,
        returnOfIncomeInvestment,
        returnOfIncomePatrimony,
        returnOfIncomeResult,
        returnOfIncomeRural,
        returnOfIncomeTaxableIncome,
        returnOfIncomeUrban,
        returnOfIncomeYearIRPF,
        returnOfIncomeExploredAreaCode,
        returnOfIncomeExploredHectares
      };
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

export const getExploredAreasInfoFromCredit = (credit: Phase2['credit']): IExploredAreasInfo | undefined => {
  if (credit) {
    const { exploredAreas } = credit;

    if (exploredAreas?.length) {
      return {
        exploredAreas
      };
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

export const getFinancialInfoFromCredit = (credit: Phase2['credit']): IFinancialInformation | undefined => {
  if (credit) {
    const { financialPendency, debtSituation, financialInformation } = credit;

    if (financialPendency || debtSituation || financialInformation) {
      return {
        financialPendency,
        debtSituation,
        financialInformation
      };
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

export const getConsultingScoreInfoFromCredit = (credit: Phase2['credit']): IConsultingScore | undefined => {
  if (credit) {
    const { scoreType, scoreNumber } = credit;

    if (scoreType || scoreNumber) {
      return {
        scoreType,
        scoreNumber
      };
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

export const getNotesInfoFromCredit = (credit: Phase2['credit']): INotes | undefined => {
  if (credit) {
    const { creditOpinion, legalOpinion } = credit;

    if (creditOpinion || legalOpinion) {
      return {
        creditOpinion,
        legalOpinion
      };
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

export const getJudicialActionsInfoFromCredit = (credit: Phase2['credit']): IJudicialActionsInfo | undefined => {
  if (credit) {
    const { judicialActions } = credit;

    if (judicialActions?.length) {
      return {
        judicialActions
      };
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

const getContactName = (contactRoles: OpportunityContactRole[], taxIdentifier: string) => {
  const cleanTaxIdentifier = brazil.cleanCpfCnpjValue(taxIdentifier);
  const contact = contactRoles.find(
    role => brazil.cleanCpfCnpjValue(role.contact?.taxIdentifier ?? '') === cleanTaxIdentifier
  );
  return contact?.contact.name;
};

export const mapSerasaReportToCreditInput = (
  serasaReport?: SerasaReport,
  contactRoles?: OpportunityContactRole[]
): Pick<CreditInput, 'serasaConsultationDate' | 'serasaDebts' | 'serasaObservation'> => {
  if (!serasaReport?.reports?.length) {
    return {};
  }

  const debts: DebtInput[] = [];

  serasaReport.reports.forEach(report => {
    if (report.occurrences?.length) {
      const reportDebts = report.occurrences.map(occurrence => ({
        amount: occurrence.balance ?? 0,
        name: i18next.t(`serasaOccurrence_${occurrence.name}`),
        type: AgFinancingDebtType.SERASA,
        contact: getContactName(contactRoles ?? [], report.taxIdentifier),
        count: occurrence.count,
        details: occurrence.details
      }));
      debts.push(...reportDebts);
    }
  });
  const mostRecentReport = serasaReport.reports.reduce(
    (latest, current) => (new Date(latest?.dateReport ?? 0) > new Date(current?.dateReport ?? 0) ? latest : current),
    serasaReport.reports[0]
  );

  const hasFailedReport = serasaReport.reports.some(
    report => report.status === SerasaApiStatus.FAILED || report.status === SerasaApiStatus.UNRESOLVED
  );

  return {
    serasaConsultationDate: mostRecentReport.dateReport,
    serasaDebts: debts,
    serasaObservation: hasFailedReport
      ? i18next.t('serasaObservation_failed')
      : i18next.t('serasaObservation_notFailed')
  };
};
